import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../rtkQuery';
import { restApiV2, restApiV2FormData, restApiV2Blob } from '../axios';
import { API_ENDPOINTS } from '../constants';

export const preHQAPI = createApi({
    reducerPath: 'preHQAPI',
    baseQuery: axiosBaseQuery(restApiV2),
    tagTypes: ['Positions'],
    endpoints: (builder) => ({
        getPreHiringPositions: builder.query({
            providesTags: ['Positions'],
            query: ({ status, offset, max, readyForInternalHq } = {}) => ({
                url: API_ENDPOINTS.LIST_PRE_HIRING_POSITIONS,
                method: 'GET',
                params: {
                    status,
                    offset,
                    max,
                    readyForInternalHq,
                },
            }),
        }),
        getPreHiringPositionDetails: builder.query({
            query: (positionId) => ({
                url: `${API_ENDPOINTS.GET_PRE_HIRING_POSITION}/${positionId}`,
                method: 'POST',
            }),
        }),
        addPositionReviewComment: builder.mutation({
            query: ({ positionId, comment }) => ({
                url: `${API_ENDPOINTS.ADD_PRE_HIRING_POSITION_COMMENT}/${positionId}`,
                method: 'POST',
                data: comment,
            }),
        }),
        updatePositionDetails: builder.mutation({
            query: ({ positionId, payload }) => ({
                url: `${API_ENDPOINTS.UPDATE_PRE_HIRING_POSITION}/${positionId}`,
                method: 'POST',
                data: payload,
            }),
            invalidatesTags: ['Positions'],
        }),
        createPosition: builder.mutation({
            query: (positionData) => ({
                url: API_ENDPOINTS.CREATE_POSITION,
                method: 'POST',
                data: positionData,
            }),
            invalidatesTags: ['Positions'],
        }),
        savePositionDraft: builder.mutation({
            query: (draftData) => ({
                url: API_ENDPOINTS.SAVE_POSITION_DRAFT,
                method: 'POST',
                data: draftData,
            }),
        }),
        deployPosition: builder.mutation({
            query: (positionId) => ({
                url: `${API_ENDPOINTS.DEPLOY_POSITION}/${positionId}`,
                method: 'POST',
            }),
        }),
        getPositionDraft: builder.query({
            query: (draftId) => ({
                url: `${API_ENDPOINTS.GET_POSITION_DRAFT}/${draftId}`,
                method: 'GET',
            }),
        }),
        updatePositionDraft: builder.mutation({
            query: ({ draftId, draftData }) => ({
                url: `${API_ENDPOINTS.UPDATE_POSITION_DRAFT}/${draftId}`,
                method: 'PUT',
                data: draftData,
            }),
        }),
        // Location Manager endpoints
        getLocationManagers: builder.query({
            query: () => ({
                url: API_ENDPOINTS.COMPANY_LOCATIONS_EMPLOYEES,
                method: 'GET',
            }),
        }),
        getJobTitles: builder.query({
            query: ({ type, query }) => ({
                url: API_ENDPOINTS.JOB_AUTOCOMPLETE,
                method: 'GET',
                params: {
                    type,
                    query,
                },
            }),
        }),
        // New endpoints from service files
        addSkills: builder.mutation({
            query: (payload) => ({
                url: API_ENDPOINTS.MATCH_SKILLS_TO_ROLES,
                method: 'POST',
                data: payload,
            }),
        }),
        createPreHiringPosition: builder.mutation({
            query: (payload) => ({
                url: API_ENDPOINTS.CREATE_PRE_HIRING_POSITION,
                method: 'POST',
                data: payload,
            }),
        }),
        generateJobDescription: builder.mutation({
            query: (payload) => ({
                url: API_ENDPOINTS.GENERATE_JOB_DESCRIPTIONS,
                method: 'POST',
                data: payload,
            }),
        }),
        updateJobDescription: builder.mutation({
            query: (payload) => ({
                url: API_ENDPOINTS.UPDATE_JOB_DESCRIPTION,
                method: 'POST',
                data: payload,
            }),
        }),
        getPreHiringJD: builder.mutation({
            query: () => ({
                url: API_ENDPOINTS.FETCH_PRE_HIRING_JOB_DESCRIPTION,
                method: 'POST',
            }),
        }),
        getApprovedPosition: builder.query({
            query: ({ posId }) => ({
                url: `${API_ENDPOINTS.PRE_DRAFT_APPROVE_PRE_HIRING_POSITION}/${posId}`,
                method: 'GET',
            }),
        }),
    }),
});

// Separate API for file uploads
export const preHQFileUploadAPI = createApi({
    reducerPath: 'preHQFileUploadAPI',
    baseQuery: axiosBaseQuery(restApiV2FormData),
    endpoints: (builder) => ({
        bulkUploadPositions: builder.mutation({
            query: (payload) => ({
                url: API_ENDPOINTS.BULK_UPLOAD_POSITIONS,
                method: 'POST',
                data: payload,
            }),
        }),
        bulkEditPositions: builder.mutation({
            query: (payload) => ({
                url: API_ENDPOINTS.BULK_EDIT_POSITIONS,
                method: 'POST',
                data: payload,
            }),
        }),
        bulkApprovePositionsFormData: builder.mutation({
            query: (payload) => ({
                url: API_ENDPOINTS.PRE_HQ_BULK_APPROVE_POSITIONS,
                method: 'POST',
                data: payload,
            }),
        }),

        // Change Request Policy Roles endpoints
        preCreateRolesMappingToChangeRequestPolicy: builder.mutation({
            query: () => ({
                url: API_ENDPOINTS.PRE_HQ_PRE_CREATE_ROLES_MAPPING_TO_CHANGE_REQUEST_POLICY,
                method: 'POST',
            }),
        }),
        saveRolesMappedToChangeRequestPolicy: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.PRE_HQ_SAVE_ROLES_MAPPED_TO_CHANGE_REQUEST_POLICY,
                method: 'POST',
                data,
            }),
        }),

        // Change Request Policy endpoints
        preCreateReviewPolicy: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.PRE_HQ_PRE_CREATE_REVIEW_POLICY,
                method: 'POST',
                data,
            }),
        }),
        createReviewPolicy: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.PRE_HQ_CREATE_REVIEW_POLICY,
                method: 'POST',
                data,
            }),
        }),
        preModifyReviewPolicy: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.PRE_HQ_PRE_MODIFY_REVIEW_POLICY,
                method: 'POST',
                data,
            }),
        }),
        modifyReviewPolicy: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.PRE_HQ_MODIFY_REVIEW_POLICY,
                method: 'POST',
                data,
            }),
        }),
        listReviewPolicies: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.PRE_HQ_LIST_REVIEW_POLICIES,
                method: 'POST',
                data,
            }),
        }),
        displayReviewPolicy: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.PRE_HQ_DISPLAY_REVIEW_POLICY,
                method: 'POST',
                data,
            }),
        }),
        updatePositionsMappedToReviewPolicy: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.PRE_HQ_UPDATE_POSITIONS_MAPPED_TO_REVIEW_POLICY,
                method: 'POST',
                data,
            }),
        }),
        deleteReviewPolicy: builder.mutation({
            query: (data) => ({
                url: API_ENDPOINTS.PRE_HQ_DELETE_REVIEW_POLICY,
                method: 'POST',
                data,
            }),
        }),
    }),
});
// Separate API for file download
export const preHQdownloadFileApi = createApi({
    reducerPath: 'PreHQdownloadFileApi',
    baseQuery: axiosBaseQuery(restApiV2Blob),
    endpoints: (builder) => ({
        downloadPositionTemplate: builder.mutation({
            query: () => ({
                url: API_ENDPOINTS.BULK_UPLOAD_TEMPLATE,
                method: 'GET',
            }),
        }),
        downloadBulkEditTemplate: builder.mutation({
            query: () => ({
                url: API_ENDPOINTS.BULK_EDIT_TEMPLATE,
                method: 'GET',
            }),
        }),
    }),
});

export const {
    useGetPreHiringPositionsQuery,
    useUpdatePositionDetailsMutation,
    useGetPreHiringPositionDetailsQuery,
    useAddPositionReviewCommentMutation,
    useCreatePositionMutation,
    useSavePositionDraftMutation,
    useDeployPositionMutation,
    useGetPositionDraftQuery,
    useUpdatePositionDraftMutation,
    // Location Manager hooks
    useGetLocationManagersQuery,
    useGetJobTitlesQuery,
    // New hooks from service files
    useAddSkillsMutation,
    useCreatePreHiringPositionMutation,
    useGenerateJobDescriptionMutation,
    useUpdateJobDescriptionMutation,
    useGetPreHiringJDMutation,
    useGetApprovedPositionQuery,
} = preHQAPI;

export const {
    useBulkUploadPositionsMutation,
    useBulkEditPositionsMutation,
    useBulkApprovePositionsFormDataMutation,
    usePreCreateRolesMappingToChangeRequestPolicyMutation,
    useSaveRolesMappedToChangeRequestPolicyMutation,
    usePreCreateReviewPolicyMutation,
    useCreateReviewPolicyMutation,
    usePreModifyReviewPolicyMutation,
    useModifyReviewPolicyMutation,
    useListReviewPoliciesMutation,
    useDisplayReviewPolicyMutation,
    useUpdatePositionsMappedToReviewPolicyMutation,
    useDeleteReviewPolicyMutation,
} = preHQFileUploadAPI;

export const { useDownloadPositionTemplateMutation, useDownloadBulkEditTemplateMutation } = preHQdownloadFileApi;
